import { useState, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { AutoBuyParameter, AutoBuyState, FilterType } from '../types';

// Define a type for validation errors
type ValidationErrors = {
  [key: string]: {
    [field: string]: string;
  };
};

/**
 * Hook for managing AutoBuy parameters state and logic
 */
export const useAutoBuyParameters = (initialRules?: any[]) => {
  // State for the parameters
  const [autoBuyState, setAutoBuyState] = useState<AutoBuyState>({
    parameters: [],
    buyManagerId: ''
  });

  // This set is used to mark parameters that have been touched, so when we create a new parameter,
  // we don't mark errors prematurely
  const [touchedFields, setTouchedFields] = useState<Record<string, Set<string>>>({});

  // State to handle validation errors
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // Check if there are validation errors
  const hasErrors = useCallback(() => {
    return Object.keys(validationErrors).length > 0;
  }, [validationErrors]);

  /**
   * Create a parameter with a temporary ID for React state management
   */
  const createParameterWithTempId = useCallback((rule: any): AutoBuyParameter => {
    return {
      id: rule.id,
      tempId: uuidv4(),
      ticketsGoal: rule.ticketsGoal ?? 0,
      limitedView: rule.limitedView || false,
      quantity: rule.quantity || '',
      quantityType: rule.quantityType || FilterType.EXACT,
      section: rule.section || '',
      sectionType: rule.sectionType || FilterType.CONTAINS,
      row: rule.row || '',
      rowType: rule.rowType || FilterType.CONTAINS,
      pricePoint: rule.pricePoint || '0,0',
      pricePointType: rule.pricePointType || FilterType.RANGE,
      isDeleted: rule.isDeleted ?? false,
      ticketsGoalReached: rule.ticketsGoalReached ?? false,
      accepted: rule.accepted ?? 0,
      completed: rule.completed ?? 0
    };
  }, []);

  const markTouched = useCallback((tempId: string, field: string) => {
    setTouchedFields(prev => {
      const next = { ...prev };
      if (!next[tempId]) next[tempId] = new Set();
      next[tempId].add(field);
      return next;
    });
  }, []);

  /**
   * Create a default parameter with default values
   */
  const createDefaultParameter = useCallback((): AutoBuyParameter => ({
    tempId: uuidv4(),
    ticketsGoal: 0,
    limitedView: false,
    quantity: '',
    quantityType: FilterType.EXACT,
    section: '',
    sectionType: FilterType.CONTAINS,
    row: '',
    rowType: FilterType.CONTAINS,
    pricePoint: '0,0',
    pricePointType: FilterType.RANGE,
    isDeleted: false,
    ticketsGoalReached: false,
    accepted: 0,
    completed: 0
  }), []);

  /**
   * Update parameters from API data
   */
  const updateParametersFromData = useCallback((data: any) => {
    if (data?.rules && Array.isArray(data.rules)) {
      if (data.rules.length === 0) {
        const defaultParameter = createDefaultParameter();
        setAutoBuyState({
          parameters: [defaultParameter],
          buyManagerId: data.buyManagerId || ''
        });
        console.log('[updateParametersFromData] No rules found, creating default parameter');
      } else {
        const parameters = data.rules.map((rule: any) => createParameterWithTempId(rule));
        setAutoBuyState({
          parameters,
          buyManagerId: data.buyManagerId || ''
        });
      }
    }
  }, [createDefaultParameter, createParameterWithTempId]);

  /**
   * Extract min and max values from a numeric range string (e.g. '10,20' => {min: 10, max: 20})
   */
  const extractRangeValues = useCallback((rangeStr: string): { min: number, max: number } => {
    if (typeof rangeStr !== 'string') return { min: 0, max: 0 };
    const parts = rangeStr.split(',');
    const min = parseFloat(parts[0]?.trim() || '0');
    const max = parseFloat(parts[1]?.trim() || '0');
    return { min: isNaN(min) ? 0 : min, max: isNaN(max) ? 0 : max };
  }, []);

  /**
   * Extract min and max values from an alphanumeric range string (e.g. 'A,Z' or 'GOLD-1,GOLD-5')
   */
  const extractAlphanumericRangeValues = useCallback((rangeStr: string): { min: string, max: string } => {
    if (typeof rangeStr !== 'string') return { min: '', max: '' };
    const parts = rangeStr.split(',');
    return { min: parts[0]?.trim() || '', max: parts[1]?.trim() || '' };
  }, []);

  /**
   * Compare alphanumeric ranges for consistency (same prefix, numeric increment)
   */
  const validateRangeConsistency = useCallback((min: string, max: string) => {
    const onlyLetters = /^[^0-9]+$/;
    if (onlyLetters.test(min) && onlyLetters.test(max)) {
      return min.localeCompare(max) < 0;
    }
    const match = (v: string) => v.match(/^([^0-9]*)(\d+)$/);
    const m1 = match(min), m2 = match(max);
    if (!m1 || !m2 || m1[1] !== m2[1]) return false;
    return parseInt(m1[2], 10) < parseInt(m2[2], 10);
  }, []);

  
  /**
   * Central function to build all validation errors.
   * @param full If true, ignore touchedParams and validate every parameter.
   */
  const buildValidationErrors = useCallback((full = false): ValidationErrors => {
    const newErrors: ValidationErrors = {};

    autoBuyState.parameters.forEach(param => {
      const {
        tempId,
        ticketsGoal,
        quantity,
        quantityType,
        pricePoint,
        pricePointType,
        section,
        sectionType,
        row,
        rowType
      } = param;

      // Helper para saber si validar un campo
      const shouldValidate = (field: string) =>
        full || (touchedFields[tempId]?.has(field) ?? false);

      // Inicializamos contenedor
      if (!newErrors[tempId]) newErrors[tempId] = {};

      // Tickets Goal
      if (shouldValidate('ticketsGoal')) {
        if (isNaN(ticketsGoal) || ticketsGoal <= 0) {
          newErrors[tempId].ticketsGoal = 'Tickets Goal must be a number greater than zero';
        }
        if (ticketsGoal < param.completed) {
          newErrors[tempId].ticketsGoal = 'Tickets Goal cannot be lower than completed tickets';
        } 
      }

      // Quantity
      if (shouldValidate('quantity') && quantityType !== FilterType.DISABLE) {
        // Exact
        if (quantityType === FilterType.EXACT) {
          const qtyNum = Number(quantity);
          //before valida that string not contain only numbers
          if (/\D/.test(quantity)) {
            newErrors[tempId].quantity = 'Only numbers are allowed';
          }
          else if (isNaN(qtyNum) || qtyNum <= 0) {
            newErrors[tempId].quantity = 'Must be a number greater than zero';
          } else if (qtyNum > ticketsGoal) {
            newErrors[tempId].quantity = 'Quantity cannot exceed Tickets Goal';
          }
        }
      
        // Contains
        if (quantityType === FilterType.CONTAINS) {
          // formato básico
          if (quantity.startsWith(',') || quantity.endsWith(',')) {
            newErrors[tempId].quantity = 'Invalid format. Cannot start or end with comma';
          } else {
            const parts = quantity
              .split(',')
              .map(s => s.trim())
              .filter(s => s.length > 0);
      
            // validar cada token
            const bad = parts.find(p => {
              const v = Number(p);
              return isNaN(v) || v <= 0 || v > ticketsGoal;
            });
            if (bad) {
              newErrors[tempId].quantity =
                `At least one value is greater than Tickets Goal`;
            }
          }
        }
      
        // Range
        if (quantityType === FilterType.RANGE) {
          const [minStr, maxStr] = quantity
            .split(',')
            .map(s => s.trim());
          const min = parseFloat(minStr);
          const max = parseFloat(maxStr);
      
          if (isNaN(min) || min <= 0) {
            newErrors[tempId].quantity = 'Minimum value must be greater than zero';
          } else if (isNaN(max) || max <= 0) {
            newErrors[tempId].quantity = 'Maximum value must be greater than zero';
          } else if (max <= min) {
            newErrors[tempId].quantity = 'Maximum must be greater than minimum';
          } else if (max > ticketsGoal) {
            newErrors[tempId].quantity =
              `Maximum value cannot exceed Tickets Goal (${ticketsGoal})`;
          }
        }
      }

      // Price Point
      if (shouldValidate('pricePoint') && pricePointType !== FilterType.DISABLE) {
        if (pricePointType === FilterType.EXACT) {
          if (isNaN(Number(pricePoint)) || Number(pricePoint) <= 0) {
            newErrors[tempId].pricePoint = 'Must be a number greater than zero';
          }
        }
        if (pricePointType === FilterType.RANGE) {
          const [minStr, maxStr] = pricePoint.split(',');
          const min = parseFloat(minStr), max = parseFloat(maxStr);
          if (isNaN(min) || min <= 0) {
            newErrors[tempId].pricePoint = 'Minimum value must be greater than zero';
          } else if (isNaN(max) || max <= 0) {
            newErrors[tempId].pricePoint = 'Maximum value must be greater than zero';
          } else if (max <= min) {
            newErrors[tempId].pricePoint = 'Maximum must be greater than minimum';
          }
        }
      }

      // Section
      if (shouldValidate('section') && sectionType !== FilterType.DISABLE) {
        if (!section.trim()) {
          newErrors[tempId].section = 'Required field';
        } else if (sectionType === FilterType.EXACT && section.includes(',')) {
          newErrors[tempId].section = 'Exact filter cannot contain commas';
        } else if (sectionType === FilterType.RANGE) {
          const [minStr, maxStr] = section.split(',').map(s => s.trim());
          if (!minStr) {
            newErrors[tempId].section = 'Minimum value is required';
          } else if (!maxStr) {
            newErrors[tempId].section = 'Maximum value is required';
          } else if (!validateRangeConsistency(minStr, maxStr)) {
            newErrors[tempId].section = 'Maximum must be greater than minimum';
          }
        }
      }

      // Row
      if (shouldValidate('row') && rowType !== FilterType.DISABLE) {
        if (!row.trim()) {
          newErrors[tempId].row = 'Required field';
        } else if (rowType === FilterType.EXACT && row.includes(',')) {
          newErrors[tempId].row = 'Exact filter cannot contain commas';
        } else if (rowType === FilterType.RANGE) {
          const [minStr, maxStr] = row.split(',').map(s => s.trim());
          if (!minStr) {
            newErrors[tempId].row = 'Minimum value is required';
          } else if (!maxStr) {
            newErrors[tempId].row = 'Maximum value is required';
          } else if (!validateRangeConsistency(minStr, maxStr)) {
            newErrors[tempId].row = 'Maximum must be greater than minimum';
          }
        }
      }

      // Si no hay errores para esta fila, elimínala
      if (Object.keys(newErrors[tempId]).length === 0) {
        delete newErrors[tempId];
      }
    });

    return newErrors;
  }, [autoBuyState.parameters, touchedFields]);

  useEffect(() => {
    // cada vez que cambien los parámetros, actualizamos el UI
    setValidationErrors(buildValidationErrors());
  }, [buildValidationErrors]);

  /**
   * Handle tickets goal change
   */
  const handleTicketsGoalChange = useCallback((tempId: string, value: string) => {
    markTouched(tempId,'ticketsGoal');    
    
    // Find the current parameter
    const currentParam = autoBuyState.parameters.find(p => p.tempId === tempId);
    
    // Convert to number for comparison, but keep original string value
    const numValue = Number(value);
    
    // Handle empty or non-numeric values
    if (isNaN(numValue) && value !== '') {
      return; // Only reject non-numeric values that aren't empty
    }
    
    // If value is empty, set it to 0 or empty string based on preference
    if (value === '') {
      setAutoBuyState(prev => ({
        ...prev,
        parameters: prev.parameters.map(p =>
          p.tempId === tempId ? { ...p, ticketsGoal: 0 } : p
        )
      }));
      return;
    }
    
    // Handle case where previous value was 0 - strip leading zeros
    let valueToStore = value;
    if (currentParam && currentParam.ticketsGoal === 0 && value.startsWith('0')) {
      valueToStore = String(parseInt(value, 10));
    }
    
    // Only update the state if we passed the validation
    setAutoBuyState(prev => ({
      ...prev,
      parameters: prev.parameters.map(p =>
        p.tempId === tempId ? { ...p, ticketsGoal: Number(valueToStore) } : p
      )
    }));
  }, [autoBuyState.parameters]);

  /**
   * Handle limited view change for a specific parameter
   */
  const handleLimitedViewChange = useCallback((tempId: string, checked: boolean) => {
    markTouched(tempId,'limitedView');
    setAutoBuyState(prev => ({
      ...prev,
      parameters: prev.parameters.map(p =>
        p.tempId === tempId ? { ...p, limitedView: checked } : p
      )
    }));
  }, []);

  /**
   * Handle generic parameter change
   */
  const handleParameterChange = useCallback((tempId: string, field: string, value: any) => {
    markTouched(tempId,field);
    setAutoBuyState(prev => ({
      ...prev,
      parameters: prev.parameters.map(p =>
        p.tempId === tempId ? { ...p, [field]: value } : p
      )
    }));
  }, []);

  /**
   * Add a new parameter
   */
  const handleAddParameter = useCallback(() => {
    const newParameter: AutoBuyParameter = {
      tempId: uuidv4(),
      ticketsGoal: 0,
      limitedView: false,
      quantity: '',
      quantityType: FilterType.EXACT,
      section: '',
      sectionType: FilterType.CONTAINS,
      row: '',
      rowType: FilterType.CONTAINS,
      pricePoint: '0,0',
      pricePointType: FilterType.RANGE,
      isDeleted: false,
      ticketsGoalReached: false,
      accepted: 0,
      completed: 0
    };
    setAutoBuyState(prev => ({
      ...prev,
      parameters: [...prev.parameters, newParameter]
    }));
  }, []);

    /**
     * Remove a parameter completely from the array
     */
    const handleDeleteParameter = useCallback((tempId: string): AutoBuyParameter[] => {
      // 1) Generate the new array by filtering out the parameter to delete
      const updatedParameters = autoBuyState.parameters.filter(p => p.tempId !== tempId);

      // 2) Update the state with this filtered array
      setAutoBuyState(prev => ({
        ...prev,
        parameters: updatedParameters
      }));

      // 3) Return the updated array for immediate use
      return updatedParameters;
    }, [autoBuyState.parameters]);

   /**
   * Handle price range change for a parameter (EXACT/RANGE/CONTAINS)
   */
   const handlePriceRangeChange = useCallback((tempId: string, min: number, max: number) => {
    markTouched(tempId,'pricePoint');
    // in your central model, range strings are 'min,max'
    handleParameterChange(tempId, 'pricePoint', `${min},${max}`);
  }, [handleParameterChange]);

  const validateAllParameters = useCallback((): boolean => {
    const validateAllFields= true;
    const newErrors = buildValidationErrors(validateAllFields);
    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [buildValidationErrors]);

  /**
   * Prepare data for saving to API
   */
  const prepareDataForSaving = useCallback((parameters?: AutoBuyParameter[]) => {
    const paramsToUse = parameters ?? autoBuyState.parameters;
    return paramsToUse.map(param => ({
      id: param.id,
      ticketsGoal: param.ticketsGoal,
      limitedView: param.limitedView,
      quantity: param.quantity,
      quantityType: param.quantityType,
      section: param.section,
      sectionType: param.sectionType,
      row: param.row,
      rowType: param.rowType,
      pricePoint: param.pricePoint,
      pricePointType: param.pricePointType,
      isDeleted: param.isDeleted,
      ticketsGoalReached: param.ticketsGoalReached,
      accepted: param.accepted,
      completed: param.completed
    }));
  }, [autoBuyState.parameters]);

  return {
    // 1) State
    autoBuyState,
    validationErrors,

    // 2) helpers
    extractRangeValues,
    extractAlphanumericRangeValues,
    validateRangeConsistency,
  
    // 3) Global indicator
    hasErrors,
  
    // 4) Loading / initialization
    updateParametersFromData,
  
    // 5) Field changes
    handleTicketsGoalChange,
    handleLimitedViewChange,
    handlePriceRangeChange,
    handleParameterChange,
  
    // 6) CRUD
    handleAddParameter,
    handleDeleteParameter,
  
    // 7) Saving
    prepareDataForSaving,
    validateAllParameters,
  };
};
